import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import Styles from './StaticLineTable.module.scss';
import CarouselComponent from '../sharedCarousel/CarouselComponent';
import {
  getElementMaximumHeight,
  getNoteValues,
  setLocalStoreWithExpiry,
  viewport
} from '../../utils/utils';
import StaticProduct from './StaticProduct';
import { PegaProductRequestBase, ProductRequestBase } from '../lineTable/lineTable.types';
import {
  fetchOfferingDataLineTableV2,
  fetchOttServiceOptions,
  getVocherPromotion
} from '../../services/LineTableService';
import { LineTableBasicPropConfig } from '../../services/LineTableBasicPropConfig';
import {
  fetchFilteredProducts,
  fetchPegaProducts,
  fetchStaticProductIds,
  filterShogunProducts,
  getShogunSharedOffer,
  getTimeUntilNextTimerEvent,
  isShogunOfferResolved
} from './Product.utils';
import { StaticProductContainerSchema, StaticProductSchema } from './StaticProductDatatypes';
import Cookies from 'js-cookie';
import { LINETABLE } from '../../Analytics/AnalyticsConstants';
import { trackEligibleItem } from '../../models/Analytics/items';
import {
  NEW,
  ONE_ECOMMERCE_HARDWARE_SUMMARY,
  ULTIMATEBUNDLE,
  VCTYPE,
  VOUCHER
} from '../../utils/constants';
import { actionTypes, AppContext } from './StaticLineTableStore/StaticLineTableProvider';

/**
 * Query-string parameter name used to trigger the Shogun/rewards (loyalty) flow.
 * The value is passed to the real offer API.
 */
const SHOGUN_QUERY_PARAM = 'gloxtoken';

/**
 * Reads the Shogun/rewards token from the URL query-string. Returns null when
 * running outside the browser or when the parameter is not present.
 */
const getShogunTokenFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window?.location?.search);
  return params?.get(SHOGUN_QUERY_PARAM);
};

const StaticProductContainer: FC<StaticProductContainerSchema> = ({
  content,
  setGenericErrorModalDetails,
  metadata,
  tabIndex,
  constants,
  loginSuccess,
  activeIndex,
  mainContent,
  address,
  eligibleBundle,
  linecheckSuccessCallBack,
  selectedOption,
  basicConfigUrl,
  selectedExistingOption,
  setLoginSuccess,
  lineCheckCallBack,
  setToggleState,
  toggleState,
  noTabs,
  deviceFlow,
  deviceFlowCallBack,
  ubRatePlanObj,
  lineCheckOnClear,
  pegaIntegration,
  pegaCustomerInterest,
  useStoryblokOrder,
  isFetching,
  setIsFetching,
  isProdEnv,
  //US_31295
  toggleContent,
  localCacheDuration,
  apiCache
}) => {
  const viewPort = viewport();
  const uniqueId: keyof typeof offeringData = content?._uid;
  const [offeringData, setOfferingData] = useState<any>();
  const [filteredProducts, setFilteredProducts] = useState<any>();
  const [newDisplayContent, setNewDisplayContent] = useState<any>();
  const offerEndpoint = LineTableBasicPropConfig?.getInstance()?.getBaseUrl();
  let AuthData: any = typeof window !== undefined && Cookies.get('sunriseAuth');
  AuthData = AuthData && JSON.parse(AuthData);
  const storeContext = useContext(AppContext);
  const isMobileFlow = content?.pageType === 'MOBILE';
  // Default apiCache to false if not explicitly set
  const resolvedApiCache = apiCache !== undefined ? apiCache : false;

  /**
   * Loyalty (Shogun) flow is active only when BOTH conditions are met:
   *  1. The Storyblok parent-level attribute `isRewardsFlow` is enabled.
   *  2. The URL query-string carries a non-empty `gloxtoken` parameter.
   * Any other combination falls back to the standard offer flow.
   */
  const shogunTokenFromUrl = getShogunTokenFromUrl();
  const isShogunFlow = Boolean(mainContent?.isRewardsFlow) && Boolean(shogunTokenFromUrl);
  //US_31295
  const getPriceToggle = () => {
    if (!toggleContent || !toggleContent.enableLoggedInPrice) return null;
    const stored = localStorage.getItem('userLoggedPricesToggle');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    const isNewCustomer = toggleContent?.customers?.toLowerCase() === NEW;
    if (isNewCustomer && toggleContent?.newUserToggleEnabled) {
      return true;
    }
    if (!isNewCustomer && toggleContent?.loggedUserToggleEnabled) {
      return true;
    }
    return null;
  };
  const [pricesToggleState, setPricesToggleState] = useState<any>(getPriceToggle);
  const [runOnLoad, setRunOnLoad] = useState<boolean>(true);
  const timerRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promoPayloadSignatureRef = useRef<string>('');
  const offerDataCacheRef = useRef<Record<string, { results: any[]; timestamp: number }>>({});

  useEffect(() => {
    setTimeout(() => {
      findHeight();
    }, 100);
    window?.addEventListener('resize', findHeight);
    window?.addEventListener('load', findHeight);
    return () => {
      window.removeEventListener('resize', findHeight);
      window.removeEventListener('load', findHeight);
    };
  }, [content?.productList, filteredProducts, newDisplayContent]);

  const findHeight = () => {
    if (noTabs) {
      const activeToggles = [toggleState?.TV, toggleState?.Netflix, toggleState?.gigaSpeed].filter(
        Boolean
      ).length;
      const hasTV = toggleState?.TV;
      const hasNetflix = toggleState?.Netflix;
      const hasGigaSpeed = toggleState?.gigaSpeed;

      const tabbIndex =
        hasTV && hasNetflix && hasGigaSpeed
          ? 3
          : (hasTV && hasNetflix) ||
              (activeToggles >= 2 && !(hasGigaSpeed && !hasTV && !hasNetflix))
            ? 2
            : activeToggles === 1 || activeToggles === 2
              ? 1
              : 0;

      getElementMaximumHeight('productCardV3' + `${tabbIndex}`, filteredProducts?.length);
    } else {
      getElementMaximumHeight('productCardV3' + `${activeIndex}`, content?.productList?.length);
    }
  };

  const hardwareSessionObj = sessionStorage.getItem(ONE_ECOMMERCE_HARDWARE_SUMMARY);
  const sessionData = hardwareSessionObj && JSON.parse(hardwareSessionObj);
  const deviceUltimateBundle = content?.enableFPB;

  useEffect(() => {
    if (offeringData && Object.keys(offeringData)?.length > 0) {
      trackEligibleItem('view', offeringData[uniqueId], '', LINETABLE, null, pegaIntegration);
    }
    //filter products by pega response
    if (pegaIntegration && offeringData && Object.keys(offeringData)?.length > 0) {
      const availableProducts =
        (content?.productList?.length && content?.productList) ||
        new Array(content?.productList?.length || 3).fill(1);

      const pegaProductsIds = offeringData[uniqueId]?.map((item: any) => item?.id);

      const onlyPegaProducts = availableProducts.filter((product: any) =>
        pegaProductsIds?.includes(product.products[0].reference.name)
      );

      //sort products by storyblok vs pega
      if (useStoryblokOrder) {
        onlyPegaProducts.sort((a: any, b: any) => {
          return availableProducts.indexOf(a) - availableProducts.indexOf(b);
        });
      } else {
        onlyPegaProducts.sort((a: any, b: any) => {
          return (
            pegaProductsIds.indexOf(a.products[0].reference.name) -
            pegaProductsIds.indexOf(b.products[0].reference.name)
          );
        });
      }

      setNewDisplayContent(onlyPegaProducts);
      setIsFetching(false);
    }

    // Shogun flow: filter storyblok product list to only the products returned by the
    // Shogun offer API (matched by productId). Logic lives in filterShogunProducts (Product.utils).
    if (isShogunFlow && offeringData && Object.keys(offeringData)?.length > 0) {
      const shogunProductIds = (offeringData[uniqueId] ?? []).map((item: any) => item?.id);
      const availableProducts =
        (content?.productList?.length && content?.productList) ||
        new Array(content?.productList?.length || 3).fill(1);

      setNewDisplayContent(filterShogunProducts(availableProducts, shogunProductIds));
      setIsFetching(false);
    }
  }, [offeringData]);

  useEffect(() => {
    if (
      offeringData &&
      Object.keys(offeringData)?.length > 0 &&
      !isMobileFlow &&
      eligibleBundle?.eligibleBundle
    ) {
      trackEligibleItem(
        'available',
        offeringData[uniqueId],
        '',
        LINETABLE,
        eligibleBundle?.eligibleBundle,
        pegaIntegration
      );
    }
  }, [eligibleBundle, offeringData]);

  //US_31295
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // window.dispatchEvent(
    //   new CustomEvent('user-logged-prices-toggle-all', {
    //     detail: {
    //       auth: AuthData?.siteId ? true  : false },
    //   })
    // );
    const handleGlobalToggle = (e: any) => {
      setPricesToggleState(e.detail.value);
    };
    window.addEventListener('user-logged-prices-toggle-all', handleGlobalToggle);
    return () => {
      window.removeEventListener('user-logged-prices-toggle-all', handleGlobalToggle);
    };
  }, []);

  /**
   * Check if cached offer data is still valid based on localCacheDuration
   */
  const isCacheValid = (cacheKey: string, durationMinutes: number | undefined): boolean => {
    const cached = offerDataCacheRef.current[cacheKey];
    if (!cached) return false;

    // A duration of 0 explicitly disables cache reads and always forces a fresh request.
    if (durationMinutes === 0) return false;

    // If no duration is specified, cache never expires (until manually cleared).
    if (durationMinutes === undefined) return true;

    if (durationMinutes < 0) return true;

    const elapsedMinutes = (Date.now() - cached.timestamp) / (1000 * 60);
    return elapsedMinutes < durationMinutes;
  };

  /**
   * fetching offer response
   */
  const fetchOfferData = (forceRefresh: boolean = false) => {
    const ProductIds: ProductRequestBase[] = [];
    let pegaPayload: PegaProductRequestBase = { atlOffers: [] };
    // for getting product offer api call request and promo id list
    //existingCustomerBenefit without login
    let existingCustomer = mainContent?.enableExistingCustomer;
    let subscriptionType = mainContent?.subscriptionType;
    let bundle4PProduct = mainContent?.pageType == '4P' ? true : false;
    const enableReturnPromo = content?.isReturnPromo;
    const benefitExistingCustomer = selectedExistingOption?.length > 0 ? true : false;
    const benefitSubscriptionType = selectedExistingOption?.join(',');
    //US_31295
    subscriptionType =
      existingCustomer || pricesToggleState ? subscriptionType : benefitSubscriptionType;
    existingCustomer =
      ((existingCustomer || benefitExistingCustomer) && AuthData) ||
      pricesToggleState ||
      !toggleContent;

    if (AuthData?.siteId && toggleContent?.enableLoggedInPrice && toggleContent.customers !== NEW) {
      if (pricesToggleState == false) {
        subscriptionType = 'none';
      } else if (pricesToggleState === true) {
        subscriptionType = undefined;
      }
    }

    if (toggleContent?.enableLoggedInPrice && pricesToggleState === false) {
      existingCustomer = false;
    }
    const P2PEnabled = content?.enableP2P ? true : false;
    const cacheDuration = localCacheDuration ?? 1;
    const cacheKey = `tab-${tabIndex}`;

    // Serve from cache without triggering loading state
    if (!forceRefresh && isCacheValid(cacheKey, cacheDuration)) {
      setOfferingData({ [uniqueId]: offerDataCacheRef.current[cacheKey].results });
      return;
    }

    // Shogun flow: triggered when `gloxtoken` is present in the URL AND the
    // storyblok parent-level `isRewardsFlow` flag is enabled (see isShogunFlow).
    // All containers share a single API call via getShogunSharedOffer (Product.utils).
    if (isShogunFlow) {
      const gloxTokenId = shogunTokenFromUrl;

      if (gloxTokenId) {
        // Skip loading state when results are already in the shared cache.
        if (!isShogunOfferResolved(gloxTokenId)) {
          setIsFetching(true);
        }

        getShogunSharedOffer(gloxTokenId, uniqueId as string, offerEndpoint)
          .then((shogunResults: any[]) => {
            offerDataCacheRef.current[cacheKey] = { results: shogunResults, timestamp: Date.now() };
            setOfferingData({ [uniqueId]: shogunResults });
            setGenericErrorModalDetails({ showPopup: false });
          })
          .catch(() => {
            setGenericErrorModalDetails({
              showPopup: true,
              content: mainContent?.telesalesBlok?.[0]
            });
          })
          .finally(() => {
            setIsFetching(false);
          });
        return;
      }
    }

    setIsFetching(true);

    if (pegaIntegration) {
      pegaPayload = fetchPegaProducts(content, pegaCustomerInterest, 'LineItem');
    } else {
      fetchStaticProductIds(
        content?.productList,
        ProductIds,
        bundle4PProduct,
        mainContent?.optionId,
        //US_31295
        pricesToggleState
      );
      const newSignature = JSON.stringify(
        ProductIds.map((item: any) => ({ id: item?.id, promoIds: item?.promoIds || [] }))
      );
      promoPayloadSignatureRef.current = newSignature;
    }
    if (
      forceRefresh ||
      (tabIndex === activeIndex && !offeringData?.[uniqueId]) ||
      (tabIndex === activeIndex && loginSuccess) ||
      selectedExistingOption
    ) {
      try {
        fetchOfferingDataLineTableV2(
          pegaIntegration ? pegaPayload : ProductIds,
          content?._uid,
          offerEndpoint,
          existingCustomer,
          subscriptionType,
          enableReturnPromo,
          P2PEnabled,
          pegaIntegration,
          resolvedApiCache,
          deviceUltimateBundle
        )
          ?.then((resp: any) => {
            const results = resp?.[uniqueId as string] ?? [];
            offerDataCacheRef.current[cacheKey] = { results, timestamp: Date.now() };
            setOfferingData(resp);
            setGenericErrorModalDetails({ showPopup: false });
          })
          .catch((error: any) => {
            setGenericErrorModalDetails({
              showPopup: true,
              content: mainContent?.telesalesBlok?.[0]
            });
          })
          .finally(() => {
            setIsFetching(false);
          });
      } catch (err: any) {
        setGenericErrorModalDetails({
          showPopup: true,
          content: mainContent?.telesalesBlok?.[0]
        });
      }
    } else if (offeringData) {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (timerRefreshTimeoutRef.current) {
      clearTimeout(timerRefreshTimeoutRef.current);
      timerRefreshTimeoutRef.current = null;
    }

    const isCurrentTab =
      activeIndex === tabIndex || activeIndex === undefined || activeIndex === null;

    if (!isCurrentTab) {
      return;
    }

    const nextTimerEventDelay = (content?.productList || []).reduce(
      (minimumDelay: number, product: any) => {
        const timer = product?.timer?.[0];
        const isCounterEnabled = timer?.enableCounter;

        if (!isCounterEnabled || !timer?.startTime || !timer?.endTime) {
          return minimumDelay;
        }

        const delay = getTimeUntilNextTimerEvent(timer.startTime, timer.endTime);

        if (delay <= 0) {
          return minimumDelay;
        }

        if (minimumDelay === 0 || delay < minimumDelay) {
          return delay;
        }

        return minimumDelay;
      },
      0
    );

    if (nextTimerEventDelay <= 0) {
      return;
    }
    timerRefreshTimeoutRef.current = setTimeout(() => {
      const refreshProductIds: ProductRequestBase[] = [];
      fetchStaticProductIds(
        content?.productList,
        refreshProductIds,
        mainContent?.pageType == '4P',
        mainContent?.optionId,
        pricesToggleState
      );
      const nextSignature = JSON.stringify(
        refreshProductIds.map((item: any) => ({ id: item?.id, promoIds: item?.promoIds || [] }))
      );

      if (nextSignature === promoPayloadSignatureRef.current) {
        return;
      }

      // Clear cache before forcing refresh on timer expiry
      const cacheKey = `tab-${tabIndex}`;
      delete offerDataCacheRef.current[cacheKey];

      setOfferingData((prev: any) => {
        if (!prev || typeof prev !== 'object') {
          return prev;
        }

        return {
          ...prev,
          [uniqueId]: null
        };
      });
      fetchOfferData(true);
    }, nextTimerEventDelay);

    return () => {
      if (timerRefreshTimeoutRef.current) {
        clearTimeout(timerRefreshTimeoutRef.current);
        timerRefreshTimeoutRef.current = null;
      }
    };
  }, [activeIndex, tabIndex, content?.productList, uniqueId, offeringData]);

  /**
   * triggering offer api based for the selected tab
   */
  useEffect(() => {
    setRunOnLoad(true);
    if (activeIndex == tabIndex || activeIndex == null) {
      if (pricesToggleState !== undefined && offeringData?.[uniqueId]) {
        offeringData[uniqueId] = null;
      }
      fetchOfferData();
      setRunOnLoad(false);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (AuthData?.siteId && loginSuccess) {
      // Clear cache on login to fetch fresh data
      const cacheKey = `tab-${tabIndex}`;
      delete offerDataCacheRef.current[cacheKey];
      fetchOfferData();
      sessionStorage.removeItem('vasPopUp');
    }
  }, [loginSuccess]);

  useEffect(() => {
    if (selectedOption != undefined && selectedExistingOption != undefined) {
      // Clear cache when selection options change to fetch fresh data
      const cacheKey = `tab-${tabIndex}`;
      delete offerDataCacheRef.current[cacheKey];
      fetchOfferData();
    }
  }, [selectedOption, selectedExistingOption]);

  //US_31295
  useEffect(() => {
    if (pricesToggleState === undefined || runOnLoad) return;
    // Clear cache when price toggle changes to fetch fresh data
    const cacheKey = `tab-${tabIndex}`;
    delete offerDataCacheRef.current[cacheKey];
    if (offeringData?.[uniqueId]) {
      offeringData[uniqueId] = null;
    }
    fetchOfferData();
  }, [pricesToggleState]);

  useEffect(() => {
    //For QoQa flow
    const queryParams = window && new URLSearchParams(window?.location.search);
    const voucher: string | null = queryParams?.get(VOUCHER);
    if (
      voucher &&
      content?.productList?.[0]?.products?.[0]?.reference?.content?.product?.[0]?.productId
    ) {
      try {
        getVocherPromotion(
          content?.productList?.[0]?.products?.[0]?.reference?.content?.product?.[0]?.productId,
          voucher
        ).then((res: any) => {
          res && setLocalStoreWithExpiry(VCTYPE, { voucher: voucher, voucherType: 'qoqa' });
        });
      } catch (err: any) {}
    }

    //To trigger serviceOptions APi by category and updating it in context
    if (storeContext && tabIndex === activeIndex) {
      const { store, dispatch } = storeContext;
      let enableTvOptions = false;
      let enableTvOptionsPopUp = false;
      (content?.productList || [])?.forEach((prod: any) => {
        if (prod?.enableTVStreamingOptionsPopUp) {
          enableTvOptionsPopUp = true;
        }
        if (prod?.enableTVStreamingOptions) {
          enableTvOptions = true;
        }
      });
      if (store?.ottOptionsApi?.length === 0 && (enableTvOptions || enableTvOptionsPopUp)) {
        fetchOttServiceOptions()
          ?.then((res: any) => {
            if (res?.length > 0) {
              dispatch({ type: actionTypes?.ottOptionsApi, payload: res });
            }
          })
          .catch(() => {});
      }
    }
  }, []);

  //No Tabs content set
  let displayContent: any = [];
  useEffect(() => {
    if (noTabs) {
      displayContent = fetchFilteredProducts(content?.productList, toggleState);
      setFilteredProducts(displayContent);
    }
  }, [toggleState]);

  // sending offer api data as callback in device Flow to identify  Multimobile/fmc
  useEffect(() => {
    if (deviceFlow?.isDeviceFlow && offeringData) {
      deviceFlow?.prodcardcallbackOfferApiData(offeringData);
    }
  }, [offeringData]);

  const getProductId = (content: any) => {
    const productData = content?.products?.[0]?.reference?.content?.product?.[0];
    return productData?.productId;
  };

  useEffect(() => {
    if (noTabs && !deviceFlow?.isDeviceFlow) {
      setNewDisplayContent(filteredProducts);
    } else {
      setNewDisplayContent(
        (content?.productList?.length && content?.productList) ||
          new Array(content?.productList?.length || 3).fill(1)
      );
    }
  }, [filteredProducts]);

  return (
    <div>
      <div className={Styles['titlecontainer']}>
        {content?.title && <div className={Styles['title']}>{content?.title}</div>}
        {content?.subTitle && <div className={Styles['description']}>{content?.subTitle}</div>}
      </div>
      <div className={`linetableV3`}>
        {viewPort && newDisplayContent && newDisplayContent?.length > 0 && (
          <CarouselComponent
            carouselIndex={0}
            key={activeIndex}
            arrow={mainContent?.enableMobileArrows}
          >
            {newDisplayContent &&
              newDisplayContent?.length > 0 &&
              (newDisplayContent || [])?.map((data: StaticProductSchema, indexx: number) => {
                return (
                  <div
                    id={`productCardV3${activeIndex}`}
                    style={{ textAlign: 'center' }}
                    key={indexx}
                  >
                    <StaticProduct
                      content={pegaIntegration && isFetching && !offeringData ? {} : data}
                      offeringData={offeringData}
                      uniqueId={uniqueId}
                      storyContent={content}
                      metadata={metadata}
                      tabIndex={tabIndex}
                      cardsLength={content?.productList?.length}
                      indexKey={indexx}
                      constants={constants}
                      mainContent={mainContent}
                      linecheckSuccessCallBack={linecheckSuccessCallBack}
                      lineCheckCallBack={lineCheckCallBack}
                      address={address}
                      eligibleBundle={eligibleBundle}
                      selectedOption={selectedOption}
                      fetchOfferData={fetchOfferData}
                      basicConfigUrl={basicConfigUrl}
                      selectedExistingOption={selectedExistingOption}
                      setLoginSuccess={setLoginSuccess}
                      enableP2P={content?.enableP2P}
                      loginSuccess={loginSuccess}
                      setToggleState={setToggleState}
                      toggleState={toggleState}
                      noTabs={noTabs}
                      deviceFlow={deviceFlow}
                      deviceFlowCallBack={deviceFlowCallBack}
                      selectedRP={ubRatePlanObj?.selectedRP}
                      continueCart={ubRatePlanObj?.continueCart}
                      lineCheckOnClear={lineCheckOnClear}
                      pegaIntegration={pegaIntegration}
                      isProdEnv={isProdEnv}
                      deviceUltimateBundle={deviceUltimateBundle}
                      //US_31295
                      toggleContent={toggleContent}
                      pricesToggleState={pricesToggleState}
                      isShogunFlow={isShogunFlow}
                      shogunToken={shogunTokenFromUrl}
                    />
                  </div>
                );
              })}
          </CarouselComponent>
        )}
      </div>
    </div>
  );
};

export default StaticProductContainer;
